import './css/mobileGridControls.css';

import React, {useEffect} from 'react';
import Flex from 'react-flexview';
import {MdKeyboardArrowLeft, MdKeyboardArrowRight} from 'react-icons/md';
import _ from 'lodash';
import Clue from './ClueText';
import GridControls from './GridControls';
import GridObject from '../../lib/wrappers/GridWrapper';

const RunOnce = ({effect}) => {
  useEffect(() => {
    effect();
  }, []);
  return null;
};

export default class MobileGridControls extends GridControls {
  constructor() {
    super();
    this.state = {
      anchors: [],
      transform: {scale: 1, translateX: 0, translateY: 0},
      dbgstr: undefined,
    };
    this.prvInput = '';
    this.inputRef = React.createRef();
    this.zoomContainer = React.createRef();
    this.wasUnfocused = Date.now() - 1000;
    this.lastTouchMove = Date.now();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.transform !== this.state.transform) {
      if (this.state.anchors.length === 0) {
        this.fitOnScreen();
      }
    }
    if (prevProps.selected.r !== this.props.selected.r || prevProps.selected.c !== this.props.selected.c) {
      this.fitOnScreen(true);
    }
  }

  fitOnScreen(fitCurrentClue) {
    if (!fitCurrentClue && this.state.lastFitOnScreen > Date.now() - 100) return;

    const rect = this.zoomContainer.current.getBoundingClientRect();
    let {scale, translateX, translateY} = this.state.transform;
    const {selected, size} = this.props;

    // default scale already fits screen width; no need to zoom out further
    scale = Math.max(1, scale);

    // this shouldn't go larger than half a tile (scaled) for now; the min X/Y
    // calculations don't work when the difference between the usable size and
    // grid size are positive, but smaller than PADDING
    const PADDING = (size / 2) * scale; // px

    const usableWidth = visualViewport.width;
    const gridWidth = this.grid.cols * size * scale;
    const minX = Math.min(0, usableWidth - gridWidth - PADDING);
    const maxX = PADDING;
    translateX = Math.min(Math.max(translateX, minX), maxX);

    const usableHeight = visualViewport.height - rect.y;
    const gridHeight = this.grid.rows * size * scale;
    const minY = Math.min(0, usableHeight - gridHeight - PADDING);
    const maxY = PADDING;
    translateY = Math.min(Math.max(translateY, minY), maxY);

    if (fitCurrentClue) {
      const posX = selected.c * size;
      const posY = selected.r * size;
      const paddingX = (rect.width - this.grid.cols * size) / 2;
      const paddingY = (rect.height - this.grid.rows * size) / 2;
      const tX = (posX + paddingX) * scale;
      const tY = (posY + paddingY) * scale;
      translateX = _.clamp(translateX, -tX, rect.width - tX - size * scale);
      translateY = _.clamp(translateY, -tY, rect.height - tY - size * scale);
    }

    this.setState({
      transform: {
        scale,
        translateX,
        translateY,
      },
      lastFitOnScreen: Date.now(),
    });
  }

  centerGridX() {
    let {scale, translateX, translateY} = this.state.transform;
    const usableWidth = visualViewport.width;
    // this.props.size can't be trusted; Player.updateSize will soon recalculate
    // it using this formula
    const size = Math.floor(usableWidth / this.grid.cols);
    const gridWidth = this.grid.cols * size;
    translateX = (usableWidth - gridWidth) / 2;
    translateY = translateX;
    this.setState({transform: {scale, translateX, translateY}});
  }

  handleClueBarTouchEnd = (e) => {
    const countAsTapBuffer = 6; // px
    const touchTravelDist = Math.abs(e.pageY - this.touchingClueBarStart.pageY);
    this.touchingClueBarStart = null;
    if (touchTravelDist <= countAsTapBuffer) {
      this.flipDirection();
      this.keepFocus();
    }
  };

  handleClueBarTouchStart = (e) => {
    this.touchingClueBarStart = e.touches[0];
  };

  handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      this.props.onSetCursorLock(true);
    }
    this.lastTouchStart = Date.now();
    this.handleTouchMove(e);
  };

  handleTouchMove = (e) => {
    e.preventDefault(); // annoying -- https://www.chromestatus.com/features/5093566007214080
    e.stopPropagation();

    const transform = this.state.transform;
    const rect = this.zoomContainer.current.getBoundingClientRect();
    const previousAnchors = e.touches.length >= this.state.anchors.length && this.state.anchors;
    const anchors = _.map(e.touches, ({pageX, pageY}, i) => {
      const x = pageX - rect.x;
      const y = pageY - rect.y;
      return {
        pixelPosition: {
          x: (x - transform.translateX) / transform.scale,
          y: (y - transform.translateY) / transform.scale,
        },
        ...previousAnchors[i],
        touchPosition: {x, y},
      };
    });
    const nTransform = this.getTransform(anchors, transform);
    if (nTransform) {
      this.lastTouchMove = Date.now();
    }

    this.setState({
      anchors,
      transform: nTransform ?? this.state.transform,
    });
  };

  handleTouchEnd = (e) => {
    if (e.touches.length === 0 && this.state.anchors.length === 1 && this.lastTouchStart > Date.now() - 100) {
      this.props.onSetCursorLock(false);
      let el = e.target; // a descendant of grid for sure
      let rc;
      for (let i = 0; el && i < 20; i += 1) {
        if (el.className.includes('grid--cell')) {
          rc = el.getAttribute('data-rc');
          break;
        }
        el = el.parentElement;
      }
      if (rc) {
        const [r, c] = rc.split(' ').map((x) => Number(x));
        if (this.props.selected.r === r && this.props.selected.c === c) {
          this.props.onChangeDirection();
        } else {
          this.props.onSetSelected({r, c});
        }
      }
      this.focusKeyboard();
    }
    e.preventDefault();
    this.handleTouchMove(e);
  };

  handleRightArrowTouchEnd = (e) => {
    e.preventDefault();
    this.handleAction('tab');
    this.keepFocus();
  };

  handleLeftArrowTouchEnd = (e) => {
    e.preventDefault();
    this.handleAction('tab', true);
    this.keepFocus();
  };

  getTransform(anchors, {scale, translateX, translateY}) {
    if (!this.props.enablePan) {
      return;
    }

    const getCenterAndDistance = (point1, point2) => {
      if (!point1) {
        return {
          center: {x: 1, y: 1},
          distance: 1,
        };
      }
      if (!point2) {
        return {
          center: point1,
          distance: 1,
        };
      }
      return {
        center: {
          x: (point1.x + point2.x) / 2,
          y: (point1.y + point2.y) / 2,
        },
        distance: Math.sqrt(
          (point1.x - point2.x) * (point1.x - point2.x) + (point1.y - point2.y) * (point1.y - point2.y)
        ),
      };
    };
    const {center: pixelCenter, distance: pixelDistance} = getCenterAndDistance(
      ..._.map(anchors, ({pixelPosition}) => pixelPosition)
    );
    const {center: touchCenter, distance: touchDistance} = getCenterAndDistance(
      ..._.map(anchors, ({touchPosition}) => touchPosition)
    );
    if (anchors.length >= 2) {
      scale = touchDistance / pixelDistance;
    }

    if (anchors.length >= 1) {
      translateX = touchCenter.x - scale * pixelCenter.x;
      translateY = touchCenter.y - scale * pixelCenter.y;
    }

    return {
      scale,
      translateX,
      translateY,
    };
  }

  get grid() {
    return new GridObject(this.props.grid);
  }

  getClueAbbreviation({clueNumber = '', direction = ''} = {}) {
    return `${clueNumber}${direction.substring(0, 1).toUpperCase()}`;
  }

  getClueText({clueNumber = '', direction = ''} = {}) {
    return this.props.clues[direction]?.[clueNumber] ?? '';
  }

  get mainClue() {
    return {clueNumber: this.getSelectedClueNumber(), direction: this.props.direction};
  }

  renderGridContent() {
    const {scale, translateX, translateY} = this.state.transform;
    const style = {
      transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
      transition: this.state.anchors.length === 0 ? '.1s transform ease-out' : '',
    };
    return (
      <div
        style={{
          display: 'flex',
          flex: 1,
          flexShrink: 1,
          flexBasis: 1,
        }}
        className="mobile-grid-controls--grid-content"
        ref={(e) => {
          if (!e) return;
          e.addEventListener('touchstart', this.handleTouchStart, {passive: false});
          e.addEventListener('touchmove', this.handleTouchMove, {passive: false});
          e.addEventListener('touchend', this.handleTouchEnd, {passive: false});
          // e.addEventListener('mouseup', this.handleTouchEnd, {passive: false});
        }}
      >
        <div
          style={{display: 'flex', flexGrow: 1}}
          className="mobile-grid-controls--zoom-container"
          ref={this.zoomContainer}
        >
          <Flex grow={1} className="mobile-grid-controls--zoom-content" style={style}>
            {this.props.children}
          </Flex>
        </div>
      </div>
    );
  }

  renderClueBar() {
    return (
      <Flex className="mobile-grid-controls--clue-bar-container">
        <div
          ref={(e) => e && e.addEventListener('touchend', this.handleLeftArrowTouchEnd, {passive: false})}
          style={{display: 'flex'}}
        >
          <MdKeyboardArrowLeft className="mobile-grid-controls--intra-clue left" onClick={this.keepFocus} />
        </div>
        <div
          style={{
            display: 'flex',
            flexGrow: 1,
            alignItems: 'center',
          }}
          className="mobile-grid-controls--clue-bar"
          ref={(e) => {
            if (!e) return;
            e.addEventListener('touchstart', this.handleClueBarTouchStart, {passive: false});
            e.addEventListener('touchend', this.handleClueBarTouchEnd, {passive: false});
          }}
          onClick={this.keepFocus}
        >
          <div className="mobile-grid-controls--clue-bar--clues--container">
            <div className="mobile-grid-controls--clue-bar--main">
              <div className="mobile-grid-controls--clue-bar--number">
                <Clue text={this.getClueAbbreviation(this.mainClue)} />
              </div>
              <Flex className="mobile-grid-controls--clue-bar--text" grow={1}>
                <Clue text={this.getClueText(this.mainClue)} />
              </Flex>
            </div>
          </div>
        </div>
        <div
          ref={(e) => e && e.addEventListener('touchend', this.handleRightArrowTouchEnd, {passive: false})}
          style={{display: 'flex'}}
        >
          <MdKeyboardArrowRight className="mobile-grid-controls--intra-clue left" onClick={this.keepFocus} />
        </div>
      </Flex>
    );
  }

  focusKeyboard() {
    this.inputRef.current.selectionStart = this.inputRef.current.selectionEnd = this.inputRef.current.value.length;
    this.inputRef.current.focus();
  }

  keepFocus = () => {
    if (!this.wasUnfocused || this.wasUnfocused >= Date.now() - 500) {
      this.focusKeyboard();
    }
  };

  handleInputFocus = (e) => {
    this.focusKeyboard();
    this.setState({dbgstr: `INPUT FOCUS ${e.target.name}`});
    if (e.target.name === '1') {
      this.selectNextClue(true);
    } else if (e.target.name === '3') {
      this.selectNextClue(false);
    }
    this.wasUnfocused = null;
  };

  handleInputBlur = (e) => {
    if (e.target.name === '2') {
      this.wasUnfocused = Date.now();
    }
  };

  /**
   * There are hidden input boxes on the page, this handler listens for changes and then relays the inferred
   * user input to the crossword grid. The input box has a well-defined initial state that we always reset to:
   * It has a value of "$", and the cursor is always at the end.
   *
   * By comparing with this initial state, we can infer what the user did, i.e. if the new value is "$a" they
   * input the letter "a", if the new value is "", then they did a backspace.
   */
  handleInputChange = (e) => {
    const textArea = e.target;
    let input = textArea.value;
    this.setState({dbgstr: `INPUT IS [${input}]`});

    if (input === '') {
      this.backspace();

      // On some devices, the cursor gets stuck at position 0, even after the input box resets its value to "$".
      // To counter that, wait until after the render and then set it to the end. Use a direct reference to the
      // input in the timeout closure; the event is not reliable, nor is this.inputRef.
      setTimeout(() => (textArea.selectionStart = textArea.value.length));
      return;
    }

    // get rid of the $ at the beginning
    input = input.substring(1);
    if (input === ' ' || input === '@') {
      // hack hack
      // for some reason, email input [on ios safari & chrome mobile inspector] doesn't fire onChange at all when pressing spacebar
      this.handleAction('space');
    } else if (input === ',') {
      this.handleAction('tab');
    } else if (input === '.') {
      this.props.onPressPeriod && this.props.onPressPeriod();
    } else {
      // support gesture-based keyboards that allow inputting words at a time
      let delay = 0;
      for (const char of input) {
        if (this.validLetter(char.toUpperCase())) {
          this.setState({dbgstr: `TYPE letter ${char.toUpperCase()}`});
          if (delay) {
            setTimeout(() => {
              this.typeLetter(char.toUpperCase(), char.toUpperCase() === char, {nextClueIfFilled: true});
            }, delay);
          } else {
            this.typeLetter(char.toUpperCase(), char.toUpperCase() === char, {nextClueIfFilled: true});
          }
          delay += 20;
        }
      }
    }
  };

  handleKeyUp = (ev) => {
    this.setState({dbgstr: `[${ev.target.value}]`});
  };

  renderMobileInputs() {
    const inputProps = {
      value: '$', // This resets the input to contain just "$" on every render.
      type: 'email',
      style: {
        opacity: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        touchEvents: 'none',
        position: 'absolute',
      },
      autoComplete: 'none',
      autoCapitalize: 'none',
      onBlur: this.handleInputBlur,
      onFocus: this.handleInputFocus,
      onChange: this.handleInputChange,
    };
    const USE_TEXT_AREA = true;
    if (USE_TEXT_AREA) {
      return (
        <>
          <textarea name="1" {...inputProps} />
          <textarea name="2" ref={this.inputRef} {...inputProps} onKeyUp={this.handleKeyUp} />
          <textarea name="3" {...inputProps} />
        </>
      );
    }
    return (
      <>
        <input name="1" {...inputProps} />
        <input name="2" ref={this.inputRef} {...inputProps} onKeyUp={this.handleKeyUp} />
        <input name="3" {...inputProps} />
      </>
    );
  }

  render() {
    return (
      // eslint-disable-next-line react/no-string-refs
      <div ref="gridControls" className="mobile-grid-controls">
        {this.renderClueBar()}
        {this.renderGridContent()}
        {this.renderMobileInputs()}
        {/* {this.renderMobileKeyboard()} */}
        {this.props.enableDebug && (this.state.dbgstr || 'No message')}
        <RunOnce effect={this.centerGridX.bind(this)} />
      </div>
    );
  }
}
